import React, { useEffect, useState } from "react";
import "./sellhouse.css";
import { useNavigate } from "react-router-dom";
import API from "../../../api";
import {
  ArrowLeft,
  X,
  Map,
  MapPin,
  IndianRupee,
  ImagePlus,
  FileText,
  Lightbulb,
  ShieldCheck,
  Upload,
  CheckCircle2,
  Loader2,
  BadgeCheck,
  Ruler,
  Landmark,
  CreditCard,
} from "lucide-react";

function SellLand() {
  const navigate = useNavigate();

  /* ================= COMMON ================= */

  const [title, setTitle] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");

  /* ================= LAND-ONLY ================= */

  const [approvalType, setApprovalType] = useState("");
  const [approvalNumber, setApprovalNumber] = useState("");
  const [plotArea, setPlotArea] = useState("");
  const [landmarks, setLandmarks] = useState("");
  const [loanAvailable, setLoanAvailable] = useState(false);

  /* ================= IMAGE ================= */

  const [layoutImage, setLayoutImage] = useState(null);
  const [preview, setPreview] = useState(null);

  const [loading, setLoading] = useState(false);

  /* =================================================
     CLEAN PREVIEW URL
  ================================================= */

  useEffect(() => {
    return () => {
      if (preview) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  /* =================================================
     IMAGE HANDLER
  ================================================= */

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    if (preview) {
      URL.revokeObjectURL(preview);
    }

    setLayoutImage(file);
    setPreview(URL.createObjectURL(file));
  };

  /* =================================================
     REMOVE IMAGE
  ================================================= */

  const removeImage = () => {
    if (preview) {
      URL.revokeObjectURL(preview);
    }

    setPreview(null);
    setLayoutImage(null);
  };

  /* =================================================
     SUBMIT
  ================================================= */

  const handleSubmit = async () => {
    if (!localStorage.getItem("token")) {
      alert("Please login again");
      return;
    }

    if (!title || !city || !price) {
      alert("Title, city and price are required");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();

      // Common
      formData.append("title", title);
      formData.append("city", city);
      formData.append("state", state);
      formData.append("price", price);
      formData.append("description", description);

      // Land details
      formData.append("approvalType", approvalType);
      formData.append("approvalNumber", approvalNumber);
      formData.append("plotArea", plotArea);
      formData.append("landmarks", landmarks);
      formData.append("loanAvailable", loanAvailable);

      if (layoutImage) {
        formData.append(
          "layoutImage",
          layoutImage
        );
      }

      const res = await API.post(
        "/land/sell",
        formData,
        {
          headers: {
            "Content-Type":
              "multipart/form-data",
          },
        }
      );

      if (res.data.success) {
        alert("Land posted successfully");
        navigate("/home");
      } else {
        alert(
          res.data.message ||
            "Failed to post land"
        );
      }
    } catch (err) {
      console.error(
        "SELL LAND ERROR:",
        err
      );

      alert(
        "Server error. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sell-page">

      {/* =================================================
          TOP BAR
      ================================================= */}

      <div className="sell-topbar">

        <button
          className="sell-back-btn"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={18} />

          <span>
            Back
          </span>
        </button>

        <button
          className="sell-close-btn"
          onClick={() => navigate("/")}
          aria-label="Close"
        >
          <X size={19} />
        </button>

      </div>


      {/* =================================================
          MAIN
      ================================================= */}

      <main className="sell-main">

        {/* =================================================
            INTRO
        ================================================= */}

        <section className="sell-intro">

          <div className="sell-intro-badge">
            <Map size={15} />
            LAND LISTING
          </div>

          <h1>
            Sell your land
            <br />
            <span>the smarter way.</span>
          </h1>

          <p>
            Add accurate property information
            to help potential buyers understand
            your land and its location.
          </p>


          {/* =================================================
              BENEFITS
          ================================================= */}

          <div className="sell-benefits">

            <div className="sell-benefit">

              <div className="sell-benefit-icon">
                <BadgeCheck size={17} />
              </div>

              <div>

                <strong>
                  Show Approval Details
                </strong>

                <span>
                  Add DTCP, CMDA or other
                  applicable approval information.
                </span>

              </div>

            </div>


            <div className="sell-benefit">

              <div className="sell-benefit-icon">
                <Ruler size={17} />
              </div>

              <div>

                <strong>
                  Mention Plot Area
                </strong>

                <span>
                  Help buyers understand the
                  size of your available land.
                </span>

              </div>

            </div>


            <div className="sell-benefit">

              <div className="sell-benefit-icon">
                <MapPin size={17} />
              </div>

              <div>

                <strong>
                  Highlight Location
                </strong>

                <span>
                  Mention nearby roads, schools,
                  transport and other landmarks.
                </span>

              </div>

            </div>

          </div>


          {/* =================================================
              TIP
          ================================================= */}

          <div className="sell-tip">

            <div className="sell-tip-icon">
              <Lightbulb size={18} />
            </div>

            <div>

              <strong>
                Listing tip
              </strong>

              <p>
                Provide the exact approval number,
                plot dimensions and nearby landmarks
                whenever possible. Buyers can make
                better decisions with complete details.
              </p>

            </div>

          </div>

        </section>


        {/* =================================================
            FORM CARD
        ================================================= */}

        <section className="sell-card">

          {/* =================================================
              HEADER
          ================================================= */}

          <div className="sell-card-header">

            <div className="sell-card-icon">
              <Map size={20} />
            </div>

            <div>

              <span>
                CREATE LAND LISTING
              </span>

              <h2>
                Land Details
              </h2>

              <p>
                Add information about your
                property.
              </p>

            </div>

          </div>


          {/* =================================================
              BASIC INFORMATION
          ================================================= */}

          <div className="sell-form-section">

            <div className="sell-section-title">

              <FileText size={16} />

              <span>
                Basic Information
              </span>

            </div>


            {/* TITLE */}

            <div className="sell-form-group">

              <label>
                Land Title
                <span>*</span>
              </label>

              <div className="sell-input-wrapper">

                <Map size={17} />

                <input
                  value={title}
                  onChange={(e) =>
                    setTitle(e.target.value)
                  }
                  placeholder="e.g. DTCP Approved Residential Plot"
                />

              </div>

            </div>


            {/* LOCATION */}

            <div className="sell-form-row">

              <div className="sell-form-group">

                <label>
                  City
                  <span>*</span>
                </label>

                <div className="sell-input-wrapper">

                  <MapPin size={17} />

                  <input
                    value={city}
                    onChange={(e) =>
                      setCity(e.target.value)
                    }
                    placeholder="City"
                  />

                </div>

              </div>


              <div className="sell-form-group">

                <label>
                  State
                </label>

                <div className="sell-input-wrapper">

                  <MapPin size={17} />

                  <input
                    value={state}
                    onChange={(e) =>
                      setState(e.target.value)
                    }
                    placeholder="State"
                  />

                </div>

              </div>

            </div>


            {/* PRICE */}

            <div className="sell-form-group">

              <label>
                Expected Price
                <span>*</span>
              </label>

              <div className="sell-price-input">

                <div className="sell-price-symbol">
                  <IndianRupee size={17} />
                </div>

                <input
                  type="number"
                  value={price}
                  onChange={(e) =>
                    setPrice(e.target.value)
                  }
                  placeholder="Enter expected price"
                />

              </div>

              {price && (
                <span className="sell-price-preview">
                  ₹
                  {Number(
                    price
                  ).toLocaleString(
                    "en-IN"
                  )}
                </span>
              )}

            </div>

          </div>


          {/* =================================================
              APPROVAL DETAILS
          ================================================= */}

          <div className="sell-form-section">

            <div className="sell-section-title">

              <BadgeCheck size={16} />

              <span>
                Approval Details
              </span>

            </div>


            {/* APPROVAL TYPE */}

            <div className="sell-form-group">

              <label>
                Approval Type
              </label>

              <div className="sell-input-wrapper">

                <BadgeCheck size={17} />

                <select
                  value={approvalType}
                  onChange={(e) =>
                    setApprovalType(
                      e.target.value
                    )
                  }
                >

                  <option value="">
                    Select approval
                  </option>

                  <option value="DTCP">
                    DTCP
                  </option>

                  <option value="CMDA">
                    CMDA
                  </option>

                  <option value="Panchayat">
                    Panchayat
                  </option>

                </select>

              </div>

            </div>


            {/* APPROVAL NUMBER */}

            <div className="sell-form-group">

              <label>
                Approval Number
              </label>

              <div className="sell-input-wrapper">

                <FileText size={17} />

                <input
                  value={approvalNumber}
                  onChange={(e) =>
                    setApprovalNumber(
                      e.target.value
                    )
                  }
                  placeholder="Enter approval number"
                />

              </div>

            </div>

          </div>


          {/* =================================================
              PLOT DETAILS
          ================================================= */}

          <div className="sell-form-section">

            <div className="sell-section-title">

              <Ruler size={16} />

              <span>
                Plot Details
              </span>

            </div>


            {/* AREA */}

            <div className="sell-form-group">

              <label>
                Plot Area
              </label>

              <div className="sell-input-wrapper">

                <Ruler size={17} />

                <input
                  value={plotArea}
                  onChange={(e) =>
                    setPlotArea(
                      e.target.value
                    )
                  }
                  placeholder="e.g. 1200 sq.ft or 3 cents"
                />

              </div>

            </div>


            {/* LANDMARKS */}

            <div className="sell-form-group">

              <label>
                Nearby Landmarks
              </label>

              <div className="sell-textarea-wrapper">

                <textarea
                  value={landmarks}
                  onChange={(e) =>
                    setLandmarks(
                      e.target.value
                    )
                  }
                  placeholder="Bus stand, school, highway, hospital, railway station..."
                />

              </div>

            </div>


            {/* LOAN */}

            <label className="sell-loan-option">

              <input
                type="checkbox"
                checked={loanAvailable}
                onChange={(e) =>
                  setLoanAvailable(
                    e.target.checked
                  )
                }
              />

              <span className="sell-custom-checkbox">

                {loanAvailable && (
                  <CheckCircle2 size={15} />
                )}

              </span>

              <span className="sell-loan-content">

                <strong>
                  Loan Available
                </strong>

                <small>
                  Indicate whether buyers may
                  be eligible for property financing.
                </small>

              </span>

              <CreditCard
                size={18}
                className="sell-loan-icon"
              />

            </label>

          </div>


          {/* =================================================
              IMAGE
          ================================================= */}

          <div className="sell-form-section">

            <div className="sell-section-title">

              <ImagePlus size={16} />

              <span>
                Layout / Property Image
              </span>

            </div>


            {!preview ? (

              <label className="sell-upload-area">

                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                />

                <div className="sell-upload-icon">
                  <Upload size={21} />
                </div>

                <strong>
                  Upload land or layout image
                </strong>

                <span>
                  Add a clear photo, layout or
                  property image
                </span>

                <small>
                  JPG, PNG or other image formats
                </small>

              </label>

            ) : (

              <div className="sell-preview-container">

                <img
                  src={preview}
                  alt="Land preview"
                  className="sell-image-preview"
                />

                <div className="sell-preview-overlay">

                  <div>

                    <ImagePlus size={16} />

                    <span>
                      {layoutImage?.name ||
                        "Land image"}
                    </span>

                  </div>

                  <button
                    type="button"
                    onClick={removeImage}
                  >
                    <X size={16} />
                    Remove
                  </button>

                </div>

              </div>

            )}

          </div>


          {/* =================================================
              DESCRIPTION
          ================================================= */}

          <div className="sell-form-section">

            <div className="sell-section-title">

              <FileText size={16} />

              <span>
                Additional Information
              </span>

            </div>

            <div className="sell-form-group">

              <label>
                Description
              </label>

              <textarea
                value={description}
                onChange={(e) =>
                  setDescription(
                    e.target.value
                  )
                }
                placeholder="Describe the land, road access, dimensions, surroundings, development potential and other useful details..."
              />

              <div className="sell-character-count">
                {description.length} characters
              </div>

            </div>

          </div>


          {/* =================================================
              DISCLAIMER
          ================================================= */}

          <div className="sell-disclaimer">

            <ShieldCheck size={18} />

            <p>
              Please provide accurate information.
              Civilink is a property listing platform
              and does not guarantee or legally verify
              ownership, approval status, documents,
              loan eligibility or other property claims.
              Buyers should independently verify all
              details before making a transaction.
            </p>

          </div>


          {/* =================================================
              POST
          ================================================= */}

          <button
            className="post-btn"
            onClick={handleSubmit}
            disabled={loading}
          >

            {loading ? (

              <>
                <Loader2
                  size={18}
                  className="sell-loading-icon"
                />

                Posting Land...
              </>

            ) : (

              <>
                <CheckCircle2 size={18} />

                Post Land
              </>

            )}

          </button>


          {/* =================================================
              CANCEL
          ================================================= */}

          <button
            type="button"
            className="sell-cancel-btn"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={16} />

            Cancel Post
          </button>


          <p className="sell-required-note">
            Fields marked with * are required.
          </p>

        </section>

      </main>

    </div>
  );
}

export default SellLand;